import React, { useState, useEffect, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const EditMilestoneTemplateForm = ({ template, onCancel, onUpdated }) => {
  const { tenant_id, loadingUser } = useContext(AuthContext);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form state - initialized from template prop
  const [formData, setFormData] = useState({
    template_name: template.template_name || "",
    description: template.description || "",
    customer_id: template.customer_id || "",
    is_default: template.is_default || false,
  });

  // Milestone items - initialized from template
  const [items, setItems] = useState(
    template.items && template.items.length > 0
      ? template.items.map((item) => ({
          item_id: item.item_id,
          milestone_name: item.milestone_name,
          description: item.description || "",
          sequence_order: item.sequence_order,
        }))
      : [{ milestone_name: "", description: "", sequence_order: 1 }]
  );

  // Fetch full template with items if items weren't included in props
  useEffect(() => {
    if (loadingUser || !tenant_id || !template?.template_id) return;
    
    // Only fetch if items are missing or empty
    if (template.items && template.items.length > 0) return;

    console.log("Items not in props, fetching full template...");
    secureAxios
      .get(`/api/milestone-templates/${template.template_id}`, { params: { tenant_id } })
      .then((res) => {
        if (res.data.items && res.data.items.length > 0) {
          setItems(
            res.data.items.map((item) => ({
              item_id: item.item_id,
              milestone_name: item.milestone_name,
              description: item.description || "",
              sequence_order: item.sequence_order,
            }))
          );
        }
      })
      .catch((err) => console.error("Error loading template items:", err));
  }, [tenant_id, template?.template_id, template?.items, loadingUser]);

  // Load customers
  useEffect(() => {
    if (loadingUser || !tenant_id) return;

    secureAxios
      .get("/api/customers", { params: { tenant_id } })
      .then((res) => setCustomers(res.data))
      .catch((err) => console.error("Error loading customers", err));
  }, [tenant_id, loadingUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      { milestone_name: "", description: "", sequence_order: items.length + 1 },
    ]);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      alert("A template must have at least one milestone item.");
      return;
    }
    
    const itemToRemove = items[index];
    
    // Warn if removing an existing item (has item_id)
    if (itemToRemove.item_id) {
      if (!window.confirm("This milestone item exists in the database. Are you sure you want to remove it?")) {
        return;
      }
    }
    
    const newItems = items.filter((_, i) => i !== index);
    // Reorder sequence numbers
    newItems.forEach((item, i) => {
      item.sequence_order = i + 1;
    });
    setItems(newItems);
  };

  const moveItem = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === items.length - 1)
    ) {
      return;
    }

    const newItems = [...items];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    
    // Swap items
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    
    // Update sequence orders
    newItems.forEach((item, i) => {
      item.sequence_order = i + 1;
    });
    
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.template_name.trim()) {
      setError("Template name is required.");
      return;
    }

    const validItems = items.filter((item) => item.milestone_name.trim());
    if (validItems.length === 0) {
      setError("At least one milestone item with a name is required.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        tenant_id,
        customer_id: formData.customer_id || null,
        items: validItems.map((item, index) => ({
          item_id: item.item_id || null, // Include item_id for existing items
          milestone_name: item.milestone_name.trim(),
          description: item.description?.trim() || null,
          sequence_order: index + 1,
        })),
      };

      await secureAxios.put(`/api/milestone-templates/${template.template_id}`, payload);
      
      alert("Milestone template updated successfully!");
      
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Error updating milestone template:", err);
      setError(err.response?.data?.error || "Failed to update milestone template.");
    } finally {
      setLoading(false);
    }
  };

  if (loadingUser) {
    return <p>Loading...</p>;
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Edit Milestone Template</h4>
        <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Template Details */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">
                Template Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="template_name"
                value={formData.template_name}
                onChange={handleChange}
                placeholder="e.g., Standard 3-Unit Program"
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Customer (Optional)</label>
              <select
                className="form-select"
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
              >
                <option value="">All Customers (Global Template)</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                Leave blank to make this template available for all customers.
              </small>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="2"
                placeholder="Optional description of this template..."
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="is_default"
                name="is_default"
                checked={formData.is_default}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="is_default">
                Set as default template for this customer
              </label>
            </div>
          </div>

          <hr />

          {/* Milestone Items Section */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Milestone Items</h5>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={addItem}
              >
                + Add Milestone
              </button>
            </div>

            <div className="alert alert-warning small">
              <strong>Note:</strong> Modifying milestone items may affect existing authorizations 
              that use this template. New milestones will only apply to new authorizations.
            </div>

            {items.map((item, index) => (
              <div key={item.item_id || `new-${index}`} className="card mb-2 border-secondary">
                <div className="card-body py-2">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <div className="d-flex flex-column">
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={() => moveItem(index, "up")}
                          disabled={index === 0}
                          title="Move up"
                        >
                          &uarr;
                        </button>
                        <span className="badge bg-secondary my-1">{item.sequence_order}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={() => moveItem(index, "down")}
                          disabled={index === items.length - 1}
                          title="Move down"
                        >
                          &darr;
                        </button>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Milestone Name *"
                        value={item.milestone_name}
                        onChange={(e) =>
                          handleItemChange(index, "milestone_name", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-md-5">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Description (optional)"
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-auto">
                      {item.item_id && (
                        <span className="badge bg-info me-2" title="Existing item">
                          Saved
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeItem(index)}
                        title="Remove milestone"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Submit Buttons */}
          <div className="d-flex gap-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMilestoneTemplateForm;